import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AccountDeletionPreview } from '../models/account-deletion.model';

/**
 * Account API Service
 *
 * Self-service account deletion against the Hub `/auth/account` endpoints
 * (`GET /auth/account/deletion-preview`, `DELETE /auth/account`).
 *
 * "Deletion" is deactivate-and-retain for FAA compliance: access is removed
 * and a one-time recovery code is issued, but compliance records are kept.
 * See agent-os/specs/2026-05-29-account-deletion/spec.md.
 *
 * Web counterpart of crop-swipe/lib/core/api/account_api.dart — keep the
 * surface in sync if either side gains a method.
 *
 * (Recovery is admin-mediated and not exposed here — no client recover call.)
 */
@Injectable({
  providedIn: 'root'
})
export class AccountApiService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  /**
   * What a deletion would entail for the current user (blockers + orgs
   * affected). Authenticated via the HTTP interceptor; no org header
   * needed (it's a `/auth` route).
   */
  async deletionPreview(): Promise<AccountDeletionPreview> {
    const response = await firstValueFrom(
      this.http.get<{ success: boolean; data: AccountDeletionPreview }>(
        `${this.apiUrl}/auth/account/deletion-preview`
      )
    );
    return response.data;
  }

  /**
   * Deletes (deactivates) the current account and returns the one-time
   * recovery code. The Hub re-validates preconditions and returns `409`
   * with a preview payload if the account isn't deletable; callers should
   * handle that by reloading the preview.
   */
  async deleteAccount(): Promise<string> {
    const response = await firstValueFrom(
      this.http.delete<{ success: boolean; data: { recoveryCode?: string } }>(
        `${this.apiUrl}/auth/account`
      )
    );
    return response.data?.recoveryCode ?? '';
  }
}
